/**
 * Lo Swiperone esiste per un limite (prob. temporaneo) di SwiperJS:
 * quando si cambiano le dimensioni effettive della slide attiva (width),
 * SwiperJS perde i riferimenti sugli offset della transizione alla slide precedente/successiva
 */

export default class Swiperone {
  constructor(container, conf) {
    this.container = container;
    this.wrapper = null;
    this.slides = null;
    this.isDraggable = conf.draggable;
    this.hasPagination = conf.pagination;      
    
    this.currentSlide = 0;
    this.maxOffsetWidth = 0;
    this.currentOffset = 0;
    this.swipingOffset = 20;
    this.slidesGap = 20;
    this.swiping = false;
    this.isTouching = false;
    this.startX = 0;
    this.endX = 0;
    this.startY = 0;
    this.endY = 0;
    this.fireFoxTransition = null;

    if (this.container) {
      this.wrapper = this.container.querySelector(".swiper-wrapper");
      this.slides = this.container.querySelectorAll(".swiper-slide");
      this.btnNext = this.container.querySelector(".js-swiper-events-button-next");
      this.btnPrev = this.container.querySelector(".js-swiper-events-button-prev");
      this.excerpt = this.container.querySelector(conf.excerpt);
      this.btnLink = this.container.querySelector(conf.btnLink);      
    }
  }

  init() {
    Array.from(Object.keys(this))?.forEach((key) => {
      if (this[key] !== "fireFoxTransition") {
        if (this[key] === null || this[key] === undefined) return;
      }
    });

    if (!this.slides || this.slides?.length < 1) {
      this.container.closest("section:not(.section-hub-events)")?.remove();
    }
    
    this._setupHeightsAndMaxOffsetWidth();
    this._showDescAndLink();
    this._initOnResize();

    this.isDraggable ? this._initSwipeOnDrag() : this._initSwipeOnClick();
    
    if (this.hasPagination) this._initPagination();

    this.btnNext?.addEventListener("click", this._swipeNext.bind(this));
    this.btnPrev?.addEventListener("click", this._swipePrev.bind(this));
    this.btnPrev?.classList.add("is-disabled");   
  }

  _initOnResize() {
    // reset values with debounce
    let resizing;
    let currentWidth = window.innerWidth;
    window.addEventListener("resize", (e) => {
      clearTimeout(resizing);
      resizing = setTimeout(() => {
        if (window.innerWidth !== currentWidth) {
          currentWidth = window.innerWidth;

          this.wrapper.style.transform = "none";
          this.maxOffsetWidth = 0;
          this.currentSlide = 0;
          this.btnNext?.classList.remove("is-disabled");
          this.btnPrev?.classList.remove("is-disabled");
          this._setupHeightsAndMaxOffsetWidth();
          this._showDescAndLink();
          if (window.innerWidth < 992) {
            this.swipingOffset = 60;
          } else {
            this.swipingOffset = 20;
          }
        }
      }, 250);
    });
  }

  _setupHeightsAndMaxOffsetWidth() {
    const heights = new Array();
    const maxHeights = new Array();

    const gaps = this.slidesGap * (this.slides.length - 1);   

    this.slides?.forEach((slide, i) => {
      this.wrapper.style.height = "auto";
      slide.style.height = "fit-content";
      slide.classList.remove("has-transition");
      slide.classList.remove("swiper-slide-active");

      heights.push(slide.offsetHeight);
      slide.classList.add("swiper-slide-active");
      maxHeights.push(slide.offsetHeight);

      if (i !== 0) {
        slide.classList.remove("swiper-slide-active");
        this.maxOffsetWidth += slide.offsetWidth;
      }

      slide.classList.add("has-transition");
    });

    this.maxOffsetWidth += gaps;

    heights.sort((a, b) => a - b);
    maxHeights.sort((a, b) => a - b);
    
    this.slides?.forEach((slide, i) => {
      if (i !== 0) {
        slide.style.height = `${heights[heights.length - 1]}px`;
      }
    });

    this.wrapper.style.height = `${maxHeights[maxHeights.length - 1]}px`; 

    if (this.slides?.length === 1) {
      this.container?.querySelector(".swiper-buttons-wrap")?.remove();
    }
  }

  _initSwipeOnClick() {
    this.slides?.forEach((slide, i) => {
      slide.addEventListener("click", (e) => {
        if (!slide.classList.contains("swiper-slide-active")) {
          this.container.querySelector(".swiper-slide-active")?.classList.remove("swiper-slide-active");
          this.currentSlide = i;        
          slide.classList.add("swiper-slide-active");
          this.wrapper.style.transform = `translate3d(-${slide.offsetLeft}px, 0, 0)`;  
          this._showDescAndLink();
        }
      });
    }); 
  }

  _initSwipeOnDrag() {
    this.container?.addEventListener("mousedown", this._handleMouseDown.bind(this));
    this.container?.addEventListener("mousemove", this._handleMouseMove.bind(this));
    this.container?.addEventListener("mouseup", this._handleMouseUp.bind(this));
  
    this.container?.addEventListener("touchstart", this._handleMouseDown.bind(this));
    this.container?.addEventListener("touchmove", this._handleMouseMove.bind(this));
    this.container?.addEventListener("touchend", this._handleMouseUp.bind(this));
  }
  
  _handleMouseDown(e) {
    if (e?.type !== "touchstart") e.preventDefault();    

    if (!this.swiping) {
      this.swiping = true;
      // fix FireFox "jump" effect
      this.wrapper.style.transition = "unset";

      if (!document.querySelector("body")?.classList.contains("has-touch-action-none")) document.querySelector("body")?.classList.add("has-touch-action-none");      
      
      if (e.clientX) {
        this.isTouching = false;
        this.startX = e.clientX;
        this.startY = e.clientY;
        
      } else if (e.touches[0].clientX) {
        this.isTouching = true;
        this.startX = e.touches[0].clientX;
        this.startY = e.touches[0].clientY;                
      } 

    } else {
      // ?
      this.swiping = false;
    }
  }

  _handleMouseMove(e) {    
    if (e.type === "touchmove") {
      if (Math.abs(e.touches[0].clientY - this.startY) > 20) {
        document.querySelector("body")?.classList.remove("has-touch-action-none");
      }
    }

    if (this.swiping) {
      this.currentOffset = this.container.querySelector(`.swiper-slide[data-slide="slide-${this.currentSlide}"]`).offsetLeft;

      this.slides?.forEach((slide) => {
        slide.style.pointerEvents = "none";
      });

      if (!this.isTouching) {
        this.wrapper.style.transform = `translate3d(${this.currentOffset > 0 ? e.clientX - this.startX - this.currentOffset : e.clientX - this.startX}px, 0, 0)`;      
        
      } else {
        this.wrapper.style.transform = `translate3d(${this.currentOffset > 0 ? e.changedTouches[0].clientX - this.startX - this.currentOffset : e.changedTouches[0].clientX - this.startX}px, 0, 0)`;
      }
    }
  }

  _handleMouseUp(e) {
    if (this.swiping) {
      if (!this.isTouching) {
        this.endX = e.clientX;
        this.endY = e.clientY;

      } else {
        this.endX = e.changedTouches[0].clientX;
        this.endY = e.changedTouches[0].clientY;
      }

      this._handleGesture();
    }    
  }

  _handleGesture() {
    if (Math.abs(this.startX - this.endX) >= this.swipingOffset) {
      clearTimeout(this.fireFoxTransition);
      
      if (this.startX > this.endX) {
        // Firefox fix
        this.fireFoxTransition = setTimeout(() => {
          this._swipeNext();
        }, 0);
        
      } else {
        // Firefox fix
        this.fireFoxTransition = setTimeout(() => {
          this._swipePrev();
        }, 0);
      }
      
    } else {
      if (this.swiping) {        
        this.wrapper.style.transform = `translate3d(-${this.currentOffset}px, 0, 0)`;
      }
    }

    this.swiping = false;
    this.slides?.forEach((slide) => {
      slide.style.pointerEvents = "all";
    });
    this.wrapper.style.transition = "all ease-out .3s";
    document.querySelector("body")?.classList.remove("has-touch-action-none");
  }

  _initPagination() {
    const paginationCont = document.createElement("div");
    paginationCont.classList.add("pagination-cont");
    
    this.slides?.forEach((slide, i) => {
      const bullet = document.createElement("span");
      bullet.classList.add("bullet");
      bullet.setAttribute("data-slide", `slide-${i}`);

      if (i === 0) bullet.classList.add("is-current");    

      paginationCont.appendChild(bullet);
    });    
    
    this.container.querySelector(".js-current-entry-extra")?.after(paginationCont);
  }

  _swipeNext(e) {        
    this.container.querySelector(`.swiper-slide-active`)?.classList.remove("swiper-slide-active");
    this.container.querySelector(".bullet.is-current")?.classList.remove("is-current");
    if (this.currentSlide + 1 <= this.slides?.length - 1) {
      this.currentSlide++;      
    } 
    
    const nextSlide = this.container.querySelector(`.swiper-slide[data-slide="slide-${this.currentSlide}"]`);
    if (nextSlide) {
      if (this.currentSlide === this.slides?.length - 1) {
        this.currentOffset = this.maxOffsetWidth;
        this.btnNext?.classList.add("is-disabled");
        
      } else {
        this.currentOffset += nextSlide.offsetWidth + this.slidesGap;        
      }
      
      this.btnPrev?.classList.remove("is-disabled");
      
      nextSlide.classList.add("swiper-slide-active");            
      this.wrapper.style.transform = `translate3d(-${this.currentOffset}px, 0, 0)`;
      this._showDescAndLink();
      this.container.querySelector(`.bullet[data-slide="slide-${this.currentSlide}"]`).classList.add("is-current");
    }    
  }

  _swipePrev(e) {   
    this.container.querySelector(`.swiper-slide-active`)?.classList.remove("swiper-slide-active");
    this.container.querySelector(".bullet.is-current")?.classList.remove("is-current");
    if (this.currentSlide > 0) {
      this.currentSlide--;      
    } 
    
    const prevSlide = this.container.querySelector(`.swiper-slide[data-slide="slide-${this.currentSlide}"]`);
    if (prevSlide) {
      if (this.currentSlide === 0) {
        this.btnPrev?.classList.add("is-disabled"); 
      }

      this.btnNext?.classList.remove("is-disabled");        

      this.currentOffset = prevSlide.offsetLeft;
      prevSlide.classList.add("swiper-slide-active");
      this.wrapper.style.transform = `translate3d(-${this.currentOffset}px, 0, 0)`;
      this._showDescAndLink();
      this.container.querySelector(`.bullet[data-slide="slide-${this.currentSlide}"]`).classList.add("is-current");
    }    
  }

  _showDescAndLink() {
    if (this.excerpt) {    
      const desc = this.container.querySelector(".swiper-slide-active .entry")?.dataset.desc;
      if (desc?.length) {
        this.excerpt.innerHTML = desc;
      }
    }
    
    if (this.btnLink) {
      const location = this.container.querySelector(".swiper-slide-active .entry")?.dataset.location;
      if (location?.length) {
        this.btnLink.href = location;
      }
    }
  }
}